import React, { useMemo, useState } from 'react';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import ToggleButton from '../../components/common/toggle-button';
import DropDown from '../../components/common/selection-drop-down';
import { useAllMinimalClients } from '../../hooks/useClients';
import {
  FeatureFlag,
  useFeatureFlagsList,
  useToggleFeatureFlag,
  useToggleFeatureFlagForClient,
} from '../../hooks/useFeatureFlags';

const FeatureFlagsPage: React.FC = () => {
  const { data: flagsData, isLoading } = useFeatureFlagsList();
  const { data: clients } = useAllMinimalClients();

  const [selectedClientId, setSelectedClientId] = useState<string>('');

  const toggleFlag = useToggleFeatureFlag();
  const toggleClientFlag = useToggleFeatureFlagForClient();

  const flags: FeatureFlag[] = useMemo(() => {
    if (!flagsData?.data) return [];
    return Object.values(flagsData.data).sort((a, b) => a.key.localeCompare(b.key));
  }, [flagsData]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header heading="Feature Flags" />
      <div className="mx-auto max-w-6xl py-6 sm:px-6 lg:px-8">
        <div className="mb-4 grid grid-cols-1 gap-4">
          <div className="flex items-end justify-between">
            <div className="w-72">
              <DropDown Label="Filter by client (optional)" value={selectedClientId} setValue={setSelectedClientId}>
                <option value="">All clients (no client override)</option>
                {(clients || [])
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
              </DropDown>
            </div>
          </div>
        </div>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="overflow-hidden rounded-md bg-white shadow">
            <div className="grid grid-cols-12 border-b bg-gray-100 px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">
              <div className="col-span-2">Key</div>
              <div className="col-span-4">Description</div>
              <div className="col-span-2">On</div>
              <div className="col-span-2">Client</div>
              <div className="col-span-2">Enabled Clients?</div>
            </div>
            <ul className="divide-y divide-gray-200">
              {flags.map((ff) => {
                const clientIdNum = selectedClientId ? parseInt(selectedClientId) : undefined;
                const clientEnabled = clientIdNum ? (ff.client_ids || []).includes(clientIdNum) : false;
                const disableClientToggle = !clientIdNum;
                return (
                  <li key={ff.id} className="grid grid-cols-12 items-center px-6 py-4">
                    <div className="col-span-2 truncate pr-4 font-mono text-sm text-gray-800">{ff.key}</div>
                    <div className="col-span-4 pr-4 text-sm text-gray-700">{ff.description}</div>
                    <div className="col-span-2">
                      <ToggleButton
                        id={`global-${ff.key}`}
                        status={!!ff.enabled}
                        setStatus={() => toggleFlag.mutate(ff.key)}
                      />
                    </div>
                    <div className="col-span-2">
                      <ToggleButton
                        id={`client-${ff.key}`}
                        status={clientEnabled}
                        disabled={disableClientToggle}
                        setStatus={() => clientIdNum && toggleClientFlag.mutate({ key: ff.key, clientId: clientIdNum })}
                      />
                      {!clientIdNum && <p className="mt-1 text-xs text-gray-400">Select a client to toggle</p>}
                    </div>
                    <div className="col-span-2">
                      {ff.client_ids &&
                        ff.client_ids.map((client_id) => {
                          const client = clients?.find((client) => client.id === client_id);
                          return <div className="mx-2 my-2 text-xs text-gray-400">{client?.name}</div>;
                        })}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureFlagsPage;
